import { normalizeDigits, toPersianDigits } from './persian-validation';

// ----------------------------------------------------------------------

const DIGIT_FORMATS = new Set([
  'imei',
  'iranian-mobile',
  'iranian-national-code',
  'iranian-plate',
  'iranian-phone',
  'postal-code',
  'verification-code',
]);

const FORMAT_ATTRIBUTES = {
  imei: { dir: 'ltr', inputMode: 'numeric', maxLength: 17, pattern: '[۰-۹0-9 ]*' },
  'iranian-mobile': { dir: 'ltr', inputMode: 'tel', maxLength: 13, pattern: '[۰-۹0-9+ ]*' },
  'iranian-national-code': {
    dir: 'ltr',
    inputMode: 'numeric',
    maxLength: 12,
    pattern: '[۰-۹0-9 ]*',
  },
  'iranian-phone': { dir: 'ltr', inputMode: 'tel', maxLength: 13, pattern: '[۰-۹0-9+ ]*' },
  'iranian-plate': { dir: 'rtl', inputMode: 'text', maxLength: 18 },
  'postal-code': { dir: 'ltr', inputMode: 'numeric', maxLength: 11, pattern: '[۰-۹0-9-]*' },
  'verification-code': {
    dir: 'ltr',
    inputMode: 'numeric',
    maxLength: 7,
    pattern: '[۰-۹0-9 ]*',
  },
  vin: { dir: 'ltr', inputMode: 'text', maxLength: 19 },
};

function digitsOnly(value, maxLength) {
  return normalizeDigits(value).replace(/\D/g, '').slice(0, maxLength);
}

function normalizeIranianPhone(value, maxLength = 11) {
  const source = String(value ?? '').trim();
  const digits = digitsOnly(source, 14);

  if (source.startsWith('+98') || digits.startsWith('0098')) {
    return `0${digits.replace(/^0098|^98/, '')}`.slice(0, maxLength);
  }

  return digits.slice(0, maxLength);
}

function normalizePlate(value, motorcycle) {
  if (motorcycle) return digitsOnly(value, 8);

  const compact = normalizeDigits(value)
    .replace(/ایران/gu, '')
    .replace(/[\s\-–—_/|.]/g, '')
    .replace(/[^0-9\u0600-\u06ff]/g, '');
  const leadingDigits = compact.match(/^\d{0,2}/)?.[0] ?? '';
  const remainder = compact.slice(leadingDigits.length);
  const letterIndex = remainder.search(/[\u0600-\u06ff]/);

  if (letterIndex === -1) return leadingDigits;

  const letter = remainder[letterIndex];
  const trailingDigits = remainder
    .slice(letterIndex + 1)
    .replace(/\D/g, '')
    .slice(0, 5);
  return `${leadingDigits}${letter}${trailingDigits}`;
}

function groupValue(value, groupLengths, separator = ' ') {
  const groups = [];
  let offset = 0;

  groupLengths.forEach((length) => {
    const group = value.slice(offset, offset + length);
    if (group) groups.push(group);
    offset += length;
  });

  return groups.join(separator);
}

function formatPlate(value, motorcycle) {
  const normalized = normalizePlate(value, motorcycle);
  if (motorcycle) return groupValue(normalized, [3, 5], '-');

  const match = normalized.match(/^(\d{0,2})([\u0600-\u06ff]?)(\d{0,5})$/);
  if (!match) return normalized;

  const [, prefix, letter, suffix] = match;
  const cityDigits = suffix.slice(3, 5);
  return [prefix, letter, suffix.slice(0, 3), cityDigits ? `ایران ${cityDigits}` : '']
    .filter(Boolean)
    .join(' ');
}

export function normalizeStructuredInput(value, format, options = {}) {
  switch (format) {
    case 'iranian-national-code':
      return digitsOnly(value, 10);
    case 'iranian-mobile':
      return normalizeIranianPhone(value, 11);
    case 'iranian-phone':
      return normalizeIranianPhone(value, 11);
    case 'postal-code':
      return digitsOnly(value, 10);
    case 'imei':
      return digitsOnly(value, 15);
    case 'verification-code':
      return digitsOnly(value, options.length ?? 6);
    case 'vin':
      return normalizeDigits(value)
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 17);
    case 'iranian-plate':
      return normalizePlate(value, options.motorcycle);
    default:
      return String(value ?? '');
  }
}

export function formatStructuredInput(value, format, options = {}) {
  const normalized = normalizeStructuredInput(value, format, options);
  let formatted;

  switch (format) {
    case 'iranian-national-code':
      formatted = groupValue(normalized, [3, 6, 1]);
      break;
    case 'iranian-mobile':
      formatted = groupValue(normalized, [4, 3, 4]);
      break;
    case 'iranian-phone':
      formatted = groupValue(normalized, normalized.startsWith('09') ? [4, 3, 4] : [3, 4, 4]);
      break;
    case 'postal-code':
      formatted = groupValue(normalized, [5, 5], '-');
      break;
    case 'imei':
      formatted = groupValue(normalized, [8, 6, 1]);
      break;
    case 'verification-code': {
      const length = options.length ?? 6;
      const firstGroupLength = Math.ceil(length / 2);
      formatted = groupValue(normalized, [firstGroupLength, length - firstGroupLength]);
      break;
    }
    case 'vin':
      formatted = groupValue(normalized, [3, 6, 8]);
      break;
    case 'iranian-plate':
      formatted = formatPlate(normalized, options.motorcycle);
      break;
    default:
      formatted = normalized;
  }

  return options.localizeDigits === false || !DIGIT_FORMATS.has(format)
    ? formatted
    : toPersianDigits(formatted);
}

export function getStructuredInputAttributes(format, options = {}) {
  const attributes = FORMAT_ATTRIBUTES[format] ?? {};

  if (format === 'verification-code') {
    const length = options.length ?? 6;
    return { ...attributes, maxLength: length + (length > 1 ? 1 : 0) };
  }

  if (format === 'iranian-plate' && options.motorcycle) {
    return { ...attributes, inputMode: 'numeric', maxLength: 9, pattern: '[۰-۹0-9-]*' };
  }

  return attributes;
}

export function formatIranianMobile(value) {
  return formatStructuredInput(value, 'iranian-mobile');
}

export function formatIranianNationalCode(value) {
  return formatStructuredInput(value, 'iranian-national-code');
}

export function formatPostalCode(value) {
  return formatStructuredInput(value, 'postal-code');
}

export function formatImei(value) {
  return formatStructuredInput(value, 'imei');
}

export function formatIranianPlate(value, motorcycle) {
  const inferredMotorcycle =
    motorcycle ??
    (!/[\u0600-\u06ff]/.test(String(value ?? '')) && digitsOnly(value, 9).length === 8);
  return formatStructuredInput(value, 'iranian-plate', { motorcycle: inferredMotorcycle });
}
